import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * Lightweight in-memory cache for the public game on/off map.
 *
 * The map is admin-tunable (PUT /api/admin/game-settings { games_enabled }) and
 * exposed publicly at GET /api/games/list. We cache for the page lifetime so
 * every component that needs the filter doesn't re-fetch on each mount.
 */
let cached = null;
let cachedOrder = null;
let inFlight = null;

function fetchEnabledMap() {
  if (cached) return Promise.resolve({ map: cached, order: cachedOrder || [] });
  if (!inFlight) {
    inFlight = axios.get(`${API}/games/list`)
      .then(r => {
        cached = r.data?.games_enabled || {};
        cachedOrder = r.data?.games_order || [];
        return { map: cached, order: cachedOrder };
      })
      .catch(() => ({ map: {}, order: [] }))
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

export function useGamesEnabled() {
  const [map, setMap] = useState(cached || {});
  const [order, setOrder] = useState(cachedOrder || []);
  const [loaded, setLoaded] = useState(Boolean(cached));

  useEffect(() => {
    let alive = true;
    fetchEnabledMap().then(({ map: m, order: o }) => {
      if (alive) {
        setMap(m);
        setOrder(o);
        setLoaded(true);
      }
    });
    return () => { alive = false; };
  }, []);

  // `m[slug] !== false` so unknown slugs default to ON — keeps the contract
  // forward-compatible when we add new games.
  const isEnabled = (slug) => map[slug] !== false;

  // Stable sort comparator using the admin-defined order. Items not in the
  // order list fall to the end (preserve their original relative order).
  const sortByOrder = (items, slugOf = (x) => x.id) =>
    [...items].sort((a, b) => {
      const ai = order.indexOf(slugOf(a));
      const bi = order.indexOf(slugOf(b));
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

  return { isEnabled, map, order, sortByOrder, loaded };
}
